import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { CurrencyDollar, CheckCircle, Clock, Wrench } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export function MyBonusesPanel() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    const endpoint = user?.role === "comercial" ? "/admin/my-commissions" : "/admin/my-bonuses";
    axios.get(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => {});
  }, [token, user]);

  if (!data || data.count === 0) return null;

  const isCommission = user?.role === "comercial";
  const title = isCommission ? "Mis Comisiones" : "Mis Bonos";
  const items = isCommission ? data.commissions : data.services;
  const color = isCommission ? "teal" : "blue";

  return (
    <div className="bg-white rounded-xl border p-5 mb-6" data-testid="my-bonuses-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          {isCommission ? <CurrencyDollar size={20} className="text-teal-600" weight="duotone" /> : <Wrench size={20} className="text-blue-600" weight="duotone" />}
          {title} — {MONTHS[(data.month || 1) - 1]} {data.year}
        </h3>
        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">{data.count} registro(s)</span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-3`}>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Total</p>
          <p className={`text-lg font-bold text-${color}-700`}>{fmt(data.total_commission)}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-[10px] text-slate-500 uppercase font-semibold flex items-center gap-1"><CheckCircle size={10} /> Autorizado</p>
          <p className="text-lg font-bold text-emerald-700">{fmt(data.authorized)}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-[10px] text-slate-500 uppercase font-semibold flex items-center gap-1"><Clock size={10} /> Pendiente</p>
          <p className="text-lg font-bold text-amber-700">{fmt(data.pending)}</p>
        </div>
      </div>

      {/* Recent items */}
      <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="bonuses-list">
        {items.slice(0, 8).map(item => (
          <div key={item.id} className="flex items-center justify-between p-2 border border-slate-100 rounded hover:bg-slate-50 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-slate-400 shrink-0">{item.date}</span>
              <span className="font-medium text-slate-700 truncate">{isCommission ? item.equipment_name : item.service_name}</span>
              <span className="text-slate-500 truncate">{item.client_name}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`font-semibold text-${color}-700`}>{fmt(item.commission_amount)}</span>
              {item.authorized ? (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">OK</span>
              ) : (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Pend</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
